"use client"

import {
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderStack,
  PageLineTabs,
  TabCountBadge,
  getPageHeader,
  useArtifactCanvas,
  useProfile,
} from '@yeshuman/sdk'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { Bookmark, Briefcase, Building2, Search, Send } from 'lucide-react'
import { BrowseOpportunities } from './browse-opportunities'
import { SavedOpportunitiesList } from './saved-opportunities-list'
import { MyApplications } from './my-applications'
import { WatchedOrganisationsList } from './watched-organisations-list'
import { openApplicationStatusCanvas } from './open-application-status-canvas'
import { useSavedOpportunities } from '../../hooks/use-saved-opportunities'
import { useWatchedOrganisations } from '../../hooks/use-watched-organisations'
import { buildApplicationStatusMap, useMyApplications } from '../../hooks/use-my-applications'
import { hasActiveApplicationForOpportunity } from './job-application-trailing'

type JobsView = 'browse' | 'saved' | 'applied' | 'watched'

function jobsViewFromParams(searchParams: URLSearchParams): JobsView {
  const tab = searchParams.get('tab')
  if (tab === 'applied') return 'applied'
  if (tab === 'watched') return 'watched'
  // Notification deep links carry only `?application=<id>` (see registry.py
  // _candidate_application_url) — the status canvas opening is signal enough
  // without an explicit tab param.
  if (searchParams.get('application')) return 'applied'
  if (tab === 'search' || tab === 'saved') return 'saved'
  return 'browse'
}

function isCandidateJobsIndexPath(pathname: string): boolean {
  return pathname === '/candidate/jobs' || pathname === '/candidate/jobs/'
}

export function CandidateJobsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const activeView = jobsViewFromParams(searchParams)
  const { profile } = useProfile()
  const { savedOpportunities } = useSavedOpportunities()
  const { organisations: watchedOrganisations } = useWatchedOrganisations({
    enabled: Boolean(profile?.id),
  })
  const { openCanvas, closeCanvas, isOpen: canvasOpen } = useArtifactCanvas()
  const prevCanvasOpen = useRef(false)
  /** Set when leaving Applied via an in-canvas navigation (e.g. View job posting). */
  const skipClearApplicationOnCloseRef = useRef(false)

  // Unconditional once profile loads: the Applied tab and the ?application= deep link
  // both need this regardless of saved-job state.
  const { applications, refetch: refetchApplications } = useMyApplications({
    enabled: Boolean(profile?.id),
  })

  const applicationStatusMap = useMemo(
    () => buildApplicationStatusMap(applications),
    [applications],
  )

  const applicationId = searchParams.get('application')
  const selectedApplication = useMemo(
    () => applications.find((app) => app.id === applicationId) ?? null,
    [applications, applicationId],
  )

  const clearApplicationParam = useCallback(() => {
    const next = new URLSearchParams(searchParams)
    next.delete('application')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  // Open/refresh the status canvas whenever the deep-link id resolves to an application.
  useEffect(() => {
    if (!applicationId) {
      closeCanvas()
      return
    }
    if (!selectedApplication) return
    openApplicationStatusCanvas({
      application: selectedApplication,
      openCanvas,
      closeCanvas,
      onBeforeNavigateAway: () => {
        skipClearApplicationOnCloseRef.current = true
      },
      onWithdrawn: () => {
        void refetchApplications()
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, selectedApplication])

  // Dismissing the canvas on the jobs list clears the deep-link param.
  // Skip when leaving via in-canvas navigation — setSearchParams would race
  // and cancel the destination route.
  useEffect(() => {
    if (prevCanvasOpen.current && !canvasOpen && applicationId) {
      if (skipClearApplicationOnCloseRef.current) {
        skipClearApplicationOnCloseRef.current = false
      } else if (isCandidateJobsIndexPath(location.pathname)) {
        clearApplicationParam()
      }
    }
    prevCanvasOpen.current = canvasOpen
  }, [applicationId, canvasOpen, clearApplicationParam, location.pathname])

  const savedBadgeCount = useMemo(
    () =>
      savedOpportunities.filter(
        (opp) =>
          opp.is_available &&
          !hasActiveApplicationForOpportunity(applicationStatusMap, opp.id),
      ).length,
    [applicationStatusMap, savedOpportunities],
  )

  const setActiveView = useCallback(
    (view: JobsView) => {
      const next = new URLSearchParams(searchParams)
      if (view === 'saved') next.set('tab', 'saved')
      else if (view === 'applied') next.set('tab', 'applied')
      else if (view === 'watched') next.set('tab', 'watched')
      else next.delete('tab')
      // A bare `application` param also implies Applied (see jobsViewFromParams);
      // clear it so switching tabs away actually leaves Applied.
      if (view !== 'applied') next.delete('application')
      next.delete('page')
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  // Migrate legacy tab params: marketplace merged into browse; the Analysis tab became Saved.
  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab !== 'marketplace' && tab !== 'search') return
    const next = new URLSearchParams(searchParams)
    if (tab === 'marketplace') next.delete('tab')
    else next.set('tab', 'saved')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const { title, description } = getPageHeader(
    '/candidate/jobs',
    'Jobs',
    'Search jobs, save the ones you like, and track your applications — all in one place.',
  )

  const watchedBadgeCount = watchedOrganisations.length

  const jobTabItems = useMemo(
    () => [
      { value: 'browse' as const, label: 'Search', icon: Search },
      {
        value: 'saved' as const,
        label: 'Saved',
        icon: Bookmark,
        ...(savedBadgeCount > 0
          ? { badge: <TabCountBadge count={savedBadgeCount} /> }
          : {}),
      },
      { value: 'applied' as const, label: 'Applied', icon: Send },
      {
        value: 'watched' as const,
        label: 'Watched',
        icon: Building2,
        ...(watchedBadgeCount > 0
          ? { badge: <TabCountBadge count={watchedBadgeCount} /> }
          : {}),
      },
    ],
    [savedBadgeCount, watchedBadgeCount],
  )

  return (
    <PageContainer>
      <div>
        <PageHeaderStack compact>
          <PageHeader
            leading={<PageHeaderLeading />}
            title={title}
            description={description}
            icon={<Briefcase className="h-5 w-5" />}
          />

          <PageLineTabs
            value={activeView}
            onValueChange={(value) => setActiveView(value as JobsView)}
            ariaLabel="Job sections"
            items={jobTabItems}
          />
        </PageHeaderStack>

        {activeView === 'applied' ? (
          <MyApplications onBrowseJobs={() => setActiveView('browse')} />
        ) : activeView === 'watched' ? (
          <WatchedOrganisationsList onBrowseJobs={() => setActiveView('browse')} />
        ) : activeView === 'saved' ? (
          <SavedOpportunitiesList />
        ) : (
          <BrowseOpportunities />
        )}
      </div>
    </PageContainer>
  )
}
